import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Eye, EyeOff } from "lucide-react";
import MarketPlaceNewLogo from "../img/MarketPlaceNewLogo.svg";
import LockIcon from "../img/LockIcon.svg";
import UserIcon from "../img/UserIcon.svg";
import Sellerimg from "../img/Sellerimg.svg";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance";

const Login = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    loginPhoneNumber: "",
    registerPhoneNumber: "",
    password: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState("otp");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loginPhoneNumber, setLoginPhoneNumber] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [country, setCountry] = useState("in");
  const [registerPhoneNumber, setRegisterPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [otpError, setOtpError] = useState("");
  const numericDigits = loginPhoneNumber.replace(/\D/g, "");
  const isNumericInput = /^\d+$/.test(loginPhoneNumber);
  const [loading, setLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [otpMessageType, setOtpMessageType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleOtpLogin = async (mode = "login") => {
    // Determine phone or register phone number based on mode
    const phone =
      mode === "login" ? loginPhoneNumber.trim() : registerPhoneNumber.trim();

    // Clear existing errors
    setErrors((prev) => ({
      ...prev,
      [`${mode}PhoneNumber`]: "",
    }));

    // Validate the phone/email field
    if (!phone) {
      setErrors((prev) => ({
        ...prev,
        [`${mode}PhoneNumber`]: "Enter your email or mobile number to login.",
      }));
      return;
    }

    let type = "";

    // Determine if the input is a phone number or email
    if (/^\d{10,15}$/.test(phone)) {
      type = "phone";
    } else if (/^\S+@\S+\.\S+$/.test(phone)) {
      type = "email";
    } else {
      setErrors((prev) => ({
        ...prev,
        [`${mode}PhoneNumber`]:
          "Enter a valid mobile number or email to login.",
      }));
      return;
    }

    if (type === "phone" && dialCode !== "+91") {
      setOtpMessage("International users need to log in using email.");
      setOtpMessageType("error");
      setErrors((prev) => ({
        ...prev,
        [`${mode}PhoneNumber`]: "Please enter your email address to log in.",
      }));

      // Show the modal when an international phone number is entered
      showModal(
        "Enter your email address to receive the OTP. Mobile OTP is only supported for Indian numbers.",
      );

      return;
    }

    try {
      setLoading(true); // Set loading to true when the OTP request starts

      // If it's a phone number, clean it and extract the country code
      let requestData = {};
      if (type === "phone") {
        const cleanedPhone = phone.replace(/\D/g, "");
        const phoneNumber = cleanedPhone.slice(-10); // Extract last 10 digits
        const countryCode =
          "+" + cleanedPhone.slice(0, cleanedPhone.length - 10);

        requestData = {
          phone_number: phoneNumber,
          country_code: countryCode,
        };
      } else {
        // If it's an email, pass the email in the request data
        requestData = {
          phone_number: phone, // Use the email entered
        };
      }
      console.log("phone", phone);

      // Send OTP request to the backend
      const response = await api.post("api/v1/get-otp-for-login", requestData);

      // Handle success or failure based on the response
      if (
        response.data.status === "success" &&
        response.data.response_code === 201
      ) {
        setOtpMessage("OTP sent successfully");
        setOtpMessageType("success");

        setTimeout(() => {
          // Navigate to OTP page with the appropriate data (phone or email)
          navigate("/otp", {
            state: {
              phoneNumber: type === "phone" ? phone : null, // Pass phone number if it's phone login
              email: type === "email" ? phone : null, // Pass email if it's email login
            },
          });
        }, 1500);
      } else {
        setOtpMessage("Please enter your registered mobile number or email");
        setOtpMessageType("error");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setOtpMessage("Something went wrong. Please try again.");
      setOtpMessageType("error");
    }
  };

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMessage("");
    setLoginPhoneNumber("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const validationErrors = {};

    if (!phoneNumber.trim())
      validationErrors.phoneNumber = "Enter your email or mobile number.";

    if (!password.trim()) validationErrors.password = "Enter your password.";

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post("/api/login/", {
        phone_number: phoneNumber,
        password: password,
      });

      if (response.status === 200 && response.data.status === "success") {
        const { access, refresh, user_id, referral_code } = response.data.data;

        localStorage.setItem("accessToken", access);
        localStorage.setItem("refreshToken", refresh);
        localStorage.setItem("user_id", user_id);
        navigate("/dashboard", { state: { referralCode: referral_code } });
      } else {
        showModal(response.data.message || "Login failed.");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        showModal(error.response.data.message || "Invalid credentials.");
      } else {
        showModal("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row  px-5">
      {/* Left Content */}

      <div
        className="w-full lg:w-1/2 min-h-[300px] lg:min-h-screen bg-no-repeat bg-center bg-cover"
        style={{
          backgroundImage: `url(${Sellerimg})`,
          backgroundSize: "contain",
        }}
      ></div>
      {/* Right Content */}
      <div className="w-full lg:w-1/2 flex justify-center items-center p-6">
        <div className="bg-white p-6 rounded-lg w-full max-w-md">
          {/* <h2 className="text-2xl font-bold text-center text-black mb-4 font-inter">
            LOGIN
          </h2> */}
          <div className="w-full flex justify-center mb-[30px]">
            <img src={MarketPlaceNewLogo} alt="" className="w-14 h-14" />
          </div>

          <p className="text-center mb-4 font-inter text-sm text-black-600">
            Welcome to Marketplace Seller!
          </p>
          <p className="text-center mb-[30px] font-inter text-2xl text-black-600 font-semibold">
            Get started with your business email or mobile number
          </p>
          <div className="flex justify-center items-center">
            {" "}
            <form className=" w-full text-center" onSubmit={handleLogin}>
              {loginMode === "otp" ? (
                <>
                  {/* === OTP Login Mode === */}
                  <div className="flex items-center gap-2 w-full ">
                    {isNumericInput && numericDigits.length >= 3 && (
                      <div className="w-[96px] h-[44px] flex items-center border-2 border-primary rounded-md">
                        <PhoneInput
                          country={country}
                          onChange={(value, countryData) => {
                            setDialCode("+" + countryData.dialCode);
                            setCountry(countryData.countryCode);
                          }}
                          enableSearch
                          disableCountryCode={false}
                          disableDropdown={false}
                          inputStyle={{
                            width: "70px",
                            border: "none",
                            backgroundColor: "transparent",
                            height: 30,
                            fontSize: "0.75rem",
                            paddingLeft: "40px",
                          }}
                          buttonStyle={{
                            padding: "0px",
                            width: "35px",
                            height: "100%",
                            borderRadius: "3px 0 0 3px",
                          }}
                          buttonClass="!bg-transparent !border-none"
                          containerClass="!w-full !m-0 !p-0"
                          dropdownStyle={{
                            textAlign: "left", // ✅ fix alignment
                            zIndex: 9999,
                            fontSize: "14px",
                          }}
                          inputProps={{ name: "country-code", readOnly: true }}
                        />
                      </div>
                    )}

                    <div className="relative w-full">
                      {!(isNumericInput && numericDigits.length >= 3) && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black z-10">
                          <img
                            src={UserIcon}
                            className="h-5 w-5"
                            alt="User Icon"
                          />
                        </span>
                      )}

                      <input
                        type="text"
                        className="w-full px-10 pr-4 py-2 border-2 border-primary text-sm bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Enter your email or mobile number"
                        value={loginPhoneNumber}
                        maxLength={/^\d{3,}$/.test(loginPhoneNumber) ? 10 : 50}
                        onChange={(e) => {
                          let value = e.target.value.trim();
                          if (/^\d+$/.test(value)) {
                            value = value.slice(0, 15);
                          }
                          setLoginPhoneNumber(value);
                          setErrors((prev) => ({
                            ...prev,
                            loginPhoneNumber: "",
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {errors.loginPhoneNumber && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.loginPhoneNumber}
                    </p>
                  )}

                  <div className="flex flex-col">
                    <button
                      className="text-sm w-full mt-4 font-inter bg-primary text-white transition-colors duration-200  px-4 py-2 border border-2 border-primary rounded-lg font-semibold"
                      onClick={() => handleOtpLogin("login")}
                      type="button"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Login with OTP"}
                    </button>
                    <span
                      className="text-[#9367D7] mt-4 text-center font-semibold text-sm cursor-pointer mb-[88px]"
                      onClick={() => {
                        setErrors({});
                        setLoginMode("password");
                        setLoginPhoneNumber("");
                      }}
                    >
                      Login with Password
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* === Password Login Mode === */}
                  <div className="relative w-full">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black z-10">
                      <img src={UserIcon} className="h-5 w-5" alt="User Icon" />
                    </span>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2 border-2 border-primary text-sm bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter your email or mobile number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                        setPhoneNumber(e.target.value);
                      }}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.phoneNumber}
                    </p>
                  )}

                  <div className="relative w-full mt-4">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black z-10">
                      <img src={LockIcon} className="h-5 w-5" alt="Lock Icon" />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="w-full pl-10 pr-10 py-2 border-2 text-sm border-primary bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setErrors((prev) => ({ ...prev, password: "" }));
                      }}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer text-gray-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </span>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.password}
                    </p>
                  )}
                  <div className="text-xs w-full flex justify-end mt-2">
                    <span
                      className="cursor-pointer hover:text-primary text-sm  font-medium"
                      onClick={() => {
                        navigate("/forgotPassword");
                      }}
                    >
                      Forgot Password?
                    </span>{" "}
                  </div>

                  <button
                    type="submit"
                    className="text-sm my-4 w-full font-inter bg-primary text-white transition-colors duration-200  px-4 py-2 border border-2 border-primary rounded-lg  font-semibold"
                  >
                    Login
                  </button>

                  <span
                    className="text-primary text-center font-semibold text-sm mt-2 cursor-pointer"
                    onClick={() => {
                      setLoginMode("otp");
                      setPhoneNumber("");
                      setPassword("");
                    }}
                  >
                    Login with OTP
                  </span>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md max-w-sm w-full shadow-lg text-center">
            <p className="text-gray-800 mb-4">{modalMessage}</p>
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-[#9061DB] text-white rounded hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
